/**
 * React Query hooks for User Settings operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getUserSettings,
  updateUserSettings,
  type UserSettings,
  type UpdateUserSettings,
} from '../../../services/userApi';

/**
 * Hook to fetch user settings.
 */
export function useUserSettings() {
  return useQuery<UserSettings>({
    queryKey: ['userSettings'],
    queryFn: getUserSettings,
    staleTime: 10 * 60 * 1000, // Consider data fresh for 10 minutes
  });
}

/**
 * Hook to update user settings.
 * Automatically invalidates the userSettings cache on success.
 */
export function useUpdateUserSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: UpdateUserSettings) => {
      return await updateUserSettings(settings);
    },
    onSuccess: () => {
      // Invalidate and refetch user settings
      queryClient.invalidateQueries({ queryKey: ['userSettings'] });
      queryClient.invalidateQueries({ queryKey: ['portfolioTotal'] });
    },
    onError: (error: Error) => {
      console.error('Failed to update user settings:', error);
      throw error;
    },
  });
}
